import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EyeInvisibleOutlined,
  MailOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  CreateRecommendationInvitation,
  RecommendationInvitationCreated,
  SiteRecommendation,
  SiteRecommendationAudience,
  SiteRecommendationStatus,
} from '../models/site-recommendation.model';
import { SiteRecommendationService } from '../services/site-recommendation.service';
import './SiteRecommendationsPage.css';

const { Paragraph, Text, Title } = Typography;

type StatusFilter = 'ALL' | SiteRecommendationStatus;

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const statusPresentation: Record<
  SiteRecommendationStatus,
  { color: string; label: string }
> = {
  INVITED: { color: 'blue', label: 'Invited' },
  PENDING_REVIEW: { color: 'gold', label: 'Pending Review' },
  PUBLISHED: { color: 'green', label: 'Published' },
  ARCHIVED: { color: 'default', label: 'Archived' },
};

const audiencePresentation: Record<
  SiteRecommendationAudience,
  { color: string; label: string }
> = {
  RECRUITER: { color: 'blue', label: 'Recruiter' },
  JOB_SEEKER: { color: 'cyan', label: 'Job Seeker' },
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  return dateTimeFormatter.format(new Date(value));
};

const SiteRecommendationsPage: React.FC = () => {
  const [recommendations, setRecommendations] = useState<SiteRecommendation[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [createdInvitation, setCreatedInvitation] =
    useState<RecommendationInvitationCreated | null>(null);
  const [selected, setSelected] = useState<SiteRecommendation | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [form] = Form.useForm<CreateRecommendationInvitation>();

  const loadRecommendations = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRecommendations((await SiteRecommendationService.list()) ?? []);
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : 'Recommendations could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRecommendations();
  }, []);

  const counts = useMemo(
    () => ({
      invited: recommendations.filter(({ status }) => status === 'INVITED')
        .length,
      pending: recommendations.filter(
        ({ status }) => status === 'PENDING_REVIEW'
      ).length,
      published: recommendations.filter(({ status }) => status === 'PUBLISHED')
        .length,
      archived: recommendations.filter(({ status }) => status === 'ARCHIVED')
        .length,
    }),
    [recommendations]
  );

  const filtered = useMemo(
    () =>
      statusFilter === 'ALL'
        ? recommendations
        : recommendations.filter(({ status }) => status === statusFilter),
    [recommendations, statusFilter]
  );

  const replaceRecommendation = (updated: SiteRecommendation) => {
    setRecommendations((current) =>
      current.map((recommendation) =>
        recommendation.id === updated.id ? updated : recommendation
      )
    );
    setSelected(updated);
  };

  const runAction = async (
    recommendation: SiteRecommendation,
    action: 'publish' | 'unpublish' | 'archive'
  ) => {
    setMutatingId(recommendation.id);
    try {
      const updated = await SiteRecommendationService[action](
        recommendation.id
      );
      replaceRecommendation(updated);
      message.success(
        action === 'publish'
          ? 'Recommendation published.'
          : action === 'unpublish'
            ? 'Recommendation removed from the public page.'
            : 'Recommendation archived.'
      );
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'The action could not be saved.'
      );
    } finally {
      setMutatingId(null);
    }
  };

  const submitInvitation = async (values: CreateRecommendationInvitation) => {
    setInviting(true);
    try {
      const created = await SiteRecommendationService.invite(values);
      setCreatedInvitation(created);
      message.success('Private invitation sent.');
      await loadRecommendations();
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'The invitation could not be sent.'
      );
    } finally {
      setInviting(false);
    }
  };

  const closeInvitation = () => {
    setInviteOpen(false);
    setCreatedInvitation(null);
    form.resetFields();
  };

  const copyInvitation = async () => {
    if (!createdInvitation) return;
    try {
      await navigator.clipboard.writeText(createdInvitation.invitationUrl);
      message.success('Invitation link copied.');
    } catch {
      message.warning('Select and copy the link manually.');
    }
  };

  const columns: ColumnsType<SiteRecommendation> = [
    {
      title: 'Contact',
      key: 'contact',
      width: 245,
      render: (_, recommendation) => (
        <button
          type="button"
          className="site-recommendations__contact"
          onClick={() => setSelected(recommendation)}
        >
          <strong>{recommendation.fullName}</strong>
          <span>{recommendation.email}</span>
        </button>
      ),
    },
    {
      title: 'Recommendation',
      key: 'recommendation',
      render: (_, recommendation) => (
        <div className="site-recommendations__summary">
          <strong>
            {recommendation.currentPosition || 'Awaiting response'}
          </strong>
          <span>
            {recommendation.testimonial ||
              'The private request has not been completed yet.'}
          </span>
        </div>
      ),
    },
    {
      title: 'Audience',
      dataIndex: 'audience',
      key: 'audience',
      align: 'center',
      width: 130,
      render: (audience?: SiteRecommendationAudience) => (
        <Tag color={audiencePresentation[audience ?? 'RECRUITER'].color}>
          {audiencePresentation[audience ?? 'RECRUITER'].label}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 145,
      render: (status: SiteRecommendationStatus) => (
        <Tag color={statusPresentation[status].color}>
          {statusPresentation[status].label}
        </Tag>
      ),
    },
    {
      title: 'Updated',
      key: 'updated',
      align: 'center',
      width: 170,
      render: (_, recommendation) =>
        formatDate(
          recommendation.publishedAt ||
            recommendation.submittedAt ||
            recommendation.invitedAt
        ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      width: 120,
      render: (_, recommendation) => (
        <Button onClick={() => setSelected(recommendation)}>Review</Button>
      ),
    },
  ];

  const statusTabs = [
    { key: 'ALL', label: 'All', count: recommendations.length },
    { key: 'INVITED', label: 'Invited', count: counts.invited },
    { key: 'PENDING_REVIEW', label: 'Pending', count: counts.pending },
    { key: 'PUBLISHED', label: 'Published', count: counts.published },
    { key: 'ARCHIVED', label: 'Archived', count: counts.archived },
  ].map(({ key, label, count }) => ({
    key,
    label: (
      <span>
        {label} <b>{count}</b>
      </span>
    ),
  }));

  return (
    <main className="ac-page-content site-recommendations">
      <section className="site-recommendations__header">
        <div>
          <span className="site-recommendations__eyebrow">
            Site Trust &amp; Advocacy
          </span>
          <Title level={1}>Recommendations</Title>
          <Paragraph>
            Invite recruiters and job seekers, review their feedback, and
            control what appears on the public homepage.
          </Paragraph>
        </div>
        <Button
          className="site-recommendations__primary-action"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setInviteOpen(true)}
        >
          Request Recommendation
        </Button>
      </section>

      <section className="site-recommendations__metrics" aria-label="Summary">
        <article>
          <span className="site-recommendations__metric-icon">
            <MailOutlined aria-hidden />
          </span>
          <div>
            <span>Invitations Open</span>
            <strong>{counts.invited}</strong>
          </div>
        </article>
        <article>
          <span className="site-recommendations__metric-icon site-recommendations__metric-icon--warning">
            <ClockCircleOutlined aria-hidden />
          </span>
          <div>
            <span>Pending Review</span>
            <strong>{counts.pending}</strong>
          </div>
        </article>
        <article>
          <span className="site-recommendations__metric-icon site-recommendations__metric-icon--success">
            <CheckCircleOutlined aria-hidden />
          </span>
          <div>
            <span>Published</span>
            <strong>{counts.published}</strong>
          </div>
        </article>
      </section>

      <section className="site-recommendations__workspace">
        <Tabs
          activeKey={statusFilter}
          items={statusTabs}
          onChange={(key) => setStatusFilter(key as StatusFilter)}
        />
        <div className="site-recommendations__toolbar">
          <div className="site-recommendations__toolbar-copy">
            <strong>Review Queue</strong>
            <span>
              Select a recommendation to review its publication status.
            </span>
          </div>
          <span
            className="site-recommendations__result-count"
            aria-live="polite"
          >
            {filtered.length} shown
          </span>
        </div>

        {loadError ? (
          <Alert
            type="error"
            showIcon
            message="Recommendations Are Unavailable"
            description={loadError}
            action={<Button onClick={loadRecommendations}>Try Again</Button>}
          />
        ) : loading ? (
          <div className="site-recommendations__loading">
            <Spin size="large" />
            <span>Loading recommendation requests…</span>
          </div>
        ) : (
          <Table
            className="site-recommendations__table"
            rowKey="id"
            columns={columns}
            dataSource={filtered}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <div className="site-recommendations__empty">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No Recommendations Match This Status"
                  />
                  <span>
                    New requests will appear here when they are available.
                  </span>
                </div>
              ),
            }}
          />
        )}
      </section>

      <Modal
        className="site-recommendations__invite-modal"
        open={inviteOpen}
        title={
          <div className="site-recommendations__invite-title">
            <span aria-hidden>
              <SafetyCertificateOutlined />
            </span>
            <div>
              <strong>
                {createdInvitation
                  ? 'Private Invitation Created'
                  : 'Request a Recommendation'}
              </strong>
              <small>
                {createdInvitation
                  ? 'Share the secure link with the intended recipient.'
                  : 'Invite a recruiter or job seeker to share feedback.'}
              </small>
            </div>
          </div>
        }
        width={500}
        centered
        onCancel={closeInvitation}
        footer={null}
        destroyOnClose
      >
        {createdInvitation ? (
          <div className="site-recommendations__invite-success">
            <CheckCircleOutlined aria-hidden />
            <div className="site-recommendations__invite-success-copy">
              <strong>Email Queued</strong>
              <span>
                The invitation was sent to {createdInvitation.email}. This is
                the only time the one-use URL is displayed.
              </span>
            </div>
            <Input.TextArea
              value={createdInvitation.invitationUrl}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
            <div className="site-recommendations__modal-actions">
              <Button
                size="large"
                icon={<CopyOutlined />}
                onClick={copyInvitation}
              >
                Copy Link
              </Button>
              <Button size="large" type="primary" onClick={closeInvitation}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <Form
            className="site-recommendations__invite-form"
            form={form}
            layout="vertical"
            initialValues={{ audience: 'RECRUITER', expiresInDays: 7 }}
            onFinish={submitInvitation}
          >
            <div className="site-recommendations__security-note" role="note">
              <SafetyCertificateOutlined aria-hidden />
              <div>
                <strong>Private, One-Use Access</strong>
                <span>
                  The link expires automatically and never appears in site
                  navigation or search results.
                </span>
              </div>
            </div>
            <Form.Item
              name="audience"
              label="Recommendation From"
              rules={[{ required: true, message: 'Choose an audience.' }]}
            >
              <Radio.Group
                className="site-recommendations__audience-control"
                optionType="button"
                buttonStyle="solid"
                options={[
                  { label: 'Recruiter', value: 'RECRUITER' },
                  { label: 'Job Seeker', value: 'JOB_SEEKER' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="fullName"
              label="Recipient Name"
              rules={[
                { required: true, message: 'Enter the recipient name.' },
                { max: 120 },
              ]}
            >
              <Input
                size="large"
                autoComplete="name"
                placeholder="e.g. Ana Rodríguez"
              />
            </Form.Item>
            <Form.Item
              name="email"
              label="Verified Recipient Email"
              rules={[
                { required: true, message: 'Enter the recipient email.' },
                { type: 'email', message: 'Enter a valid email.' },
              ]}
            >
              <Input
                size="large"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
              />
            </Form.Item>
            <Form.Item
              name="expiresInDays"
              label="Link Expires In"
              rules={[{ required: true }]}
            >
              <InputNumber
                className="site-recommendations__expiration-control"
                size="large"
                min={1}
                max={30}
                addonAfter="days"
              />
            </Form.Item>
            <div className="site-recommendations__modal-actions site-recommendations__modal-actions--form">
              <Button size="large" onClick={closeInvitation}>
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                icon={<MailOutlined />}
                loading={inviting}
              >
                Send Invitation
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      <Drawer
        open={Boolean(selected)}
        title="Recommendation Review"
        width={520}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <div className="site-recommendations__review">
            <div className="site-recommendations__review-heading">
              <div>
                <Title level={3}>{selected.fullName}</Title>
                <Text>
                  {selected.currentPosition || 'Position not submitted'}
                  {selected.companyName ? ` · ${selected.companyName}` : ''}
                </Text>
              </div>
              <Tag color={statusPresentation[selected.status].color}>
                {statusPresentation[selected.status].label}
              </Tag>
            </div>
            <dl>
              <div>
                <dt>Recommendation From</dt>
                <dd>
                  {audiencePresentation[selected.audience ?? 'RECRUITER'].label}
                </dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{selected.email}</dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>{formatDate(selected.submittedAt)}</dd>
              </div>
              <div>
                <dt>Publication consent</dt>
                <dd>
                  {selected.publicationConsent ? 'Confirmed' : 'Not given'}
                </dd>
              </div>
            </dl>
            <blockquote>
              {selected.testimonial ||
                'This invitation has not received a response yet.'}
            </blockquote>
            <div className="site-recommendations__review-actions">
              {selected.status === 'PENDING_REVIEW' ? (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={mutatingId === selected.id}
                  onClick={() => runAction(selected, 'publish')}
                >
                  Accept and publish
                </Button>
              ) : null}
              {selected.status === 'PUBLISHED' ? (
                <Button
                  icon={<EyeInvisibleOutlined />}
                  loading={mutatingId === selected.id}
                  onClick={() => runAction(selected, 'unpublish')}
                >
                  Unpublish
                </Button>
              ) : null}
              {selected.status !== 'ARCHIVED' ? (
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={mutatingId === selected.id}
                  onClick={() => runAction(selected, 'archive')}
                >
                  Archive
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Drawer>
    </main>
  );
};

export default SiteRecommendationsPage;
